import {
    parse,
    TYPE,
    type DateElement,
    type MessageFormatElement,
    type NumberElement,
    type TimeElement,
} from '@formatjs/icu-messageformat-parser'
import { IntlMessageFormat } from 'intl-messageformat'
import { Result } from 'true-myth'

import type { Count, ValueKind, ValueOf } from '@/lib/facts/values'
import { weekdayName } from '@/i18n/calendar'
import type { Locale } from '@/i18n/routing'

import type { ValueFormatters } from './format'
import type { TextValue } from './rich-text'
import { parseStyle, type Presentation } from './variables'

/*
 * The ICU half of the message grammar, which a client island may carry: every simple
 * argument and every plural `#` is replaced by a private-use sentinel before ICU formats
 * the message, and selectors choose their branches on raw values, so formatting can emit
 * only literal copy, the branches selectors choose, and sentinels. What a sentinel stands
 * for is substituted afterwards — into markdown text nodes by `renderMessage`, into a
 * plain string by `renderTemplate` — so a value can never add structure.
 */

const SENTINEL_BASE = 0xe000
/** Private-use code points: CommonMark reads them as ordinary characters, so a
 *  sentinel takes part in no markdown construct a value could have formed. */
export const SENTINEL_PATTERN = /[-]/gu
export const sentinel = (index: number) => String.fromCodePoint(SENTINEL_BASE + index)
export const sentinelIndex = (char: string) => (char.codePointAt(0) ?? 0) - SENTINEL_BASE

/** ICU with tags ignored, so a tag reaches the markdown parse as raw markup and is
 *  refused there. Copy holding a sentinel's own code point would be read back as a
 *  value, so it does not parse either. */
export function parseMessageSource(
    source: string,
    options: { captureLocation?: boolean } = {},
): Result<MessageFormatElement[], { offset: number }> {
    const planted = source.search(SENTINEL_PATTERN)
    if (planted !== -1) {
        return Result.err({ offset: planted })
    }
    try {
        return Result.ok(parse(source, { ignoreTag: true, ...options }))
    } catch (error) {
        const offset = (error as { location?: { start: { offset: number } } }).location?.start
            .offset
        return Result.err({ offset: offset ?? 0 })
    }
}

/** An argument carrying an ICU style (`{d, number, duration-long}`), which the grammar
 *  reads as a kind and a presentation (`./variables` `parseStyle`). */
export function hasFormatStyle(
    element: MessageFormatElement,
): element is NumberElement | DateElement | TimeElement {
    return element.type === TYPE.number || element.type === TYPE.date || element.type === TYPE.time
}

/** A variable's typed value: a fact kind, or another rendered message. */
export type Variable =
    | { [K in ValueKind]: { readonly kind: K; readonly value: ValueOf<K> } }[ValueKind]
    | { readonly kind: 'message'; readonly value: TextValue }

export type Lookup =
    | { readonly status: 'present'; readonly variable: Variable }
    | { readonly status: 'absent' }
    /** A sub-template reference that leads back to a message already rendering. */
    | { readonly status: 'cyclic' }

export const MESSAGE_DROP_CAUSES = [
    'no-copy',
    'missing-variable',
    'invalid-icu',
    'invalid-link',
    'disallowed-node',
    'cyclic-reference',
    'empty',
] as const
export type MessageDropCause = (typeof MESSAGE_DROP_CAUSES)[number]

export type MessageFailure =
    | { readonly cause: 'missing-variable'; readonly missing: readonly string[] }
    | { readonly cause: Exclude<MessageDropCause, 'missing-variable'> }

export class Unrenderable extends Error {
    constructor(readonly failure: MessageFailure) {
        super(failure.cause)
    }
}
export const fail = (cause: Exclude<MessageDropCause, 'missing-variable'>): never => {
    throw new Unrenderable({ cause })
}

/** Runs a render that signals failure by throwing `Unrenderable`. */
export function attempt<T>(render: () => T): Result<T, MessageFailure> {
    try {
        return Result.ok(render())
    } catch (error) {
        if (error instanceof Unrenderable) {
            return Result.err(error.failure)
        }
        throw error
    }
}

/** What a sentinel stands for; `variable` is absent when nothing supplies it, and
 *  `presentation` is the one the argument's style named, if any. */
export interface Slot {
    readonly name: string
    readonly variable: Variable | undefined
    readonly presentation?: string
}

export interface FormattedIcu {
    /** The formatted message, every value still a sentinel. */
    readonly output: string
    readonly slots: readonly Slot[]
}

/** The message formatted by ICU with its values left as sentinels; throws `Unrenderable`
 *  when it is not ICU or a branch that renders names a variable nothing supplies. */
export function formatIcu(
    source: string,
    { locale, lookup }: { readonly locale: Locale; readonly lookup: (name: string) => Lookup },
): FormattedIcu {
    const parsed = parseMessageSource(source)
    if (parsed.isErr) {
        return fail('invalid-icu')
    }
    const placeholders = new Placeholders(lookup)
    const icu = placeholders.rewrite(parsed.value, undefined)

    let output: string
    try {
        output = String(new IntlMessageFormat(icu, locale).format(placeholders.values))
    } catch {
        return fail('invalid-icu')
    }

    const missing = [...output.matchAll(SENTINEL_PATTERN)]
        .map(([char]) => placeholders.slots[sentinelIndex(char)])
        .filter((slot) => slot.variable === undefined)
        .map((slot) => slot.name)
    if (missing.length > 0) {
        throw new Unrenderable({ cause: 'missing-variable', missing: [...new Set(missing)] })
    }
    return { output, slots: placeholders.slots }
}

/** A value as the text it reads as, formatted by its kind in the presentation its slot
 *  named — one the kind's menu holds, since the style was read against it. */
export function formatValue(
    variable: Variable,
    format: ValueFormatters,
    presentation?: string,
): string {
    const as = <K extends ValueKind>(_kind: K) => presentation as Presentation<K> | undefined
    switch (variable.kind) {
        case 'text':
        case 'href':
            return variable.value
        case 'message':
            return variable.value.text
        case 'count':
            return format.count(variable.value)
        case 'duration':
            return format.duration(variable.value, as('duration'))
        case 'distance':
            return format.distance(variable.value)
        case 'rating':
            return format.rating(variable.value)
        case 'weekday':
            return format.weekday(variable.value, as('weekday'))
        case 'clock':
            return format.clock(variable.value)
        case 'date':
            return format.date(variable.value, as('date'))
        case 'instant':
            return format.instant(variable.value, as('instant'))
        case 'country':
            return format.country(variable.value)
        case 'list':
            return format.list(variable.value)
        case 'money':
            return format.money(variable.value, as('money'))
    }
}

type Selector = string | number

/** Rewrites an ICU message so that formatting it can emit only literal copy, the
 *  branches its selectors choose, and sentinels. */
class Placeholders {
    readonly slots: Slot[] = []
    readonly values: Record<string, Selector> = {}
    private readonly resolved = new Map<string, Variable | undefined>()
    private readonly argumentSlots = new Map<string, string>()

    constructor(private readonly lookup: (name: string) => Lookup) {}

    rewrite(
        elements: MessageFormatElement[],
        plural: { name: string; offset: number } | undefined,
    ): MessageFormatElement[] {
        return elements.flatMap((element): MessageFormatElement[] => {
            switch (element.type) {
                case TYPE.literal:
                    return [element]
                case TYPE.argument:
                    return [this.argument(element.value)]
                case TYPE.number:
                case TYPE.date:
                case TYPE.time:
                    return [this.styled(element.value, element.style)]
                case TYPE.pound:
                    return plural ? [this.pound(plural)] : [{ type: TYPE.literal, value: '#' }]
                case TYPE.select:
                case TYPE.plural: {
                    const variable = this.variable(element.value)
                    const guarded = element.type === TYPE.select && 'none' in element.options
                    this.values[element.value] =
                        variable === undefined
                            ? guarded
                                ? 'none'
                                : element.type === TYPE.plural
                                  ? Number.NaN
                                  : ''
                            : selectorValue(variable)
                    const inner =
                        element.type === TYPE.plural
                            ? { name: element.value, offset: element.offset }
                            : plural
                    const options = Object.fromEntries(
                        Object.entries(element.options).map(([key, option]) => [
                            key,
                            { ...option, value: this.rewrite(option.value, inner) },
                        ]),
                    )
                    // A marker standing where the selector does, so an absent unguarded
                    // selector counts as missing only on a branch that renders.
                    const marker =
                        variable === undefined && !guarded
                            ? [this.slot(element.value, undefined)]
                            : []
                    return [...marker, { ...element, options } as MessageFormatElement]
                }
                default:
                    // Tags cannot parse with tags ignored, so nothing reaches here.
                    return fail('invalid-icu')
            }
        })
    }

    private variable(name: string): Variable | undefined {
        if (!this.resolved.has(name)) {
            const found = this.lookup(name)
            if (found.status === 'cyclic') {
                fail('cyclic-reference')
            }
            this.resolved.set(name, found.status === 'present' ? found.variable : undefined)
        }
        return this.resolved.get(name)
    }

    private argument(name: string): MessageFormatElement {
        const existing = this.argumentSlots.get(name)
        if (existing !== undefined) {
            return { type: TYPE.argument, value: existing }
        }
        const element = this.slot(name, this.variable(name))
        this.argumentSlots.set(name, element.value)
        return element
    }

    /** A styled argument: its style must be in the vocabulary and restate the value's
     *  kind; it never overrides one. Each styled use is its own slot, since two uses of
     *  one variable may present it differently. */
    private styled(name: string, style: unknown): MessageFormatElement {
        const parsed = parseStyle(style)
        if (parsed.status === 'outside') {
            return fail('invalid-icu')
        }
        const variable = this.variable(name)
        if (variable !== undefined && variable.kind !== parsed.kind) {
            return fail('invalid-icu')
        }
        return this.slot(name, variable, parsed.presentation)
    }

    private pound({ name, offset }: { name: string; offset: number }): MessageFormatElement {
        const selector = this.values[name]
        const counted =
            typeof selector === 'number' && !Number.isNaN(selector)
                ? ({ kind: 'count', value: (selector - offset) as Count } as const)
                : undefined
        return this.slot(name, counted)
    }

    private slot(name: string, variable: Variable | undefined, presentation?: string) {
        const index =
            this.slots.push(
                presentation === undefined ? { name, variable } : { name, variable, presentation },
            ) - 1
        // The key cannot collide with a selector, whose name is the variable's own.
        const key = `\u0000${index}`
        this.values[key] = sentinel(index)
        return { type: TYPE.argument, value: key } as const
    }
}

/** What a selector chooses on: a weekday by its canonical English name, which is derived
 *  from the neutral index rather than listed
 *  (→ invariants.md#facts-canonical-store-editorial). */
function selectorValue(variable: Variable): Selector {
    switch (variable.kind) {
        case 'money':
            return variable.value.amount
        case 'message':
            return variable.value.text
        case 'weekday':
            return weekdayName(variable.value, 'en-us') ?? ''
        case 'list':
            return variable.value.join(',')
        default:
            return variable.value
    }
}
